import {
	alpha,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid2,
	Theme,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import {
	ErrorModal,
	getShiftByParameters,
	MenuOptionType,
	ShiftInfo,
	useContextMenuMESF,
	useMesfRealtime,
} from "@dexteel/mesf-core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	Carrier,
	CARRIER_INITIAL_VALUES,
} from "@/pages/mill/carriers/models/Carrier";
import {
	routeCarrierAssembly,
	routeMillGantt,
} from "@/setup/routes/mill.routes";
import { WarningModal } from "@/shared/modals/WarningModal";
import { backgroundColor } from "@/utils/theme";
import { Leaderboard } from "@mui/icons-material";
import { RowClassParams, RowStyle } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useLastInstancesTableData } from "../hooks/useLastInstancesTableData";
import {
	getLastCarrierInstances,
	updateCarrierStatus,
} from "../repositories/CarrierAssemblyRepository";
import { ActionModal } from "./ActionModal";
import { assemblyStatusCodes } from "./CarrierAssemblyStatusTag";
import { CarrierCard } from "./CarrierCard";
import { PassChangeModal } from "./passes/PassChangeModal";
import { useCarrierActions } from "./useCarrierActions";
import { CarrierAction, useCarrierPolicy } from "./useCarrierPolicy";

type useLastCarrierInstancesProps = {
	partId: number;
};

export const useLastCarrierInstances = ({
	partId,
}: useLastCarrierInstancesProps) => {
	return useQuery<Carrier[], Error>({
		queryKey: ["carrierInstances", partId],
		queryFn: ({ signal }) => getLastCarrierInstances({ partId }, signal),
	});
};

type Props = {
	carrier: Carrier | null;
	modal: "assembly" | "disassembly" | "";
	onHide: ({ close, entityId }: { close: boolean; entityId?: number }) => void;
};

export const CarrierAssemblyDetails = ({ carrier, modal, onHide }: Props) => {
	const navigate = useNavigate();
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { actionsFor } = useCarrierPolicy();

	const [action, setAction] = useState<Record<string, string>>({
		action: "install",
		code: "I",
		message: "installed",
	});
	const [message, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [openInstallUninstallDialog, setOpenInstallUninstallDialog] =
		useState(false);
	const [openPassChangeDialog, setOpenPassChangeDialog] = useState(false);
	const [showAllCarriers, setShowAllCarriers] = useState(false);
	const [warningMessage, setWarningMessage] = useState("");

	const { reset } = useForm<Carrier>({
		defaultValues: CARRIER_INITIAL_VALUES,
		mode: "onChange",
	});

	const {
		data: rows,
		error: e,
		isError,
		isLoading,
	} = useLastCarrierInstances({ partId: carrier?.PartId! });

	const { columnDefs } = useLastInstancesTableData({});
	const { defaultColDef } = useGridDefinitions({});

	const getMenuOptions = (data: Carrier) => {
		let options: MenuOptionType[] = [];
		if (data) {
			options = options.concat([
				{
					name: "See on Gantt",
					key: "see_instance_on_gantt",
					onClick: async () => {
						const resp = await getShiftByParameters({
							productionDate: moment(data.InstalledAt).toDate(),
							shiftId: 0,
						});
						if (resp.ok) {
							const shiftId = (resp.data as ShiftInfo).CurrentShiftId;
							window.open(
								`${routeMillGantt}/${shiftId}?carrierInstanceId=${data.CarrierInstanceId}`,
								"_blank",
							);
						} else {
							setError(resp.message);
						}
					},
					icon: <Leaderboard />,
				},
			]);
		}
		return options;
	};

	const getRowStyle = (params: RowClassParams): RowStyle | undefined => {
		if ((params.data as Carrier).CreatedAt === carrier?.CreatedAt) {
			return { backgroundColor };
		}
	};

	const queryClient = useQueryClient();
	const updateStatus = useMutation(
		({ newStatus }: { newStatus: string }) => {
			return updateCarrierStatus({
				partId: carrier?.PartId!,
				newStatus,
			});
		},
		{
			onSuccess: async () => {
				await queryClient.invalidateQueries(["carriersAssembly"]);
				await queryClient.invalidateQueries(["carrierInstances"]);
				setNotificationMessage(
					`The carrier was ${action["message"]} successfully`,
				);
				onHide({
					close: false,
				});
			},
			onError: (error: Error) => {
				setError(error.message);
			},
		},
	);

	const { actions } = useCarrierActions({
		carrier,
		updateStatus,
		setAction,
		setOpenInstallUninstallDialog,
		setOpenPassChangeDialog,
		setOpenConfirmDialog,
	});

	const visibleActions = actionsFor(carrier?.CarrierStatusCode).filter(
		({ key }) => !!carrier && (actions[key].isVisible?.(carrier) ?? true),
	);
	const flowActions = visibleActions.filter(
		({ key }) => !actions[key].destructive,
	);
	const destructiveActions = visibleActions.filter(
		({ key }) => actions[key].destructive,
	);

	const renderAction = (
		{ key, allowed, requirementLabel }: CarrierAction,
		emphasis: "primary" | "secondary" | "destructive",
	) => {
		const definition = actions[key];
		const { variant, sx } = {
			primary: {
				variant: "contained" as const,
				sx: { px: 4, fontSize: "0.95rem", fontWeight: 600 },
			},
			secondary: {
				variant: "outlined" as const,
				sx: {
					px: 3,
					backgroundColor: (theme: Theme) =>
						alpha(theme.palette.primary.main, 0.08),
					borderColor: (theme: Theme) =>
						alpha(theme.palette.primary.main, 0.24),
					"&:hover": {
						backgroundColor: (theme: Theme) =>
							alpha(theme.palette.primary.main, 0.16),
						borderColor: (theme: Theme) =>
							alpha(theme.palette.primary.main, 0.4),
					},
				},
			},
			destructive: {
				variant: "text" as const,
				sx: {
					px: 2,
					color: "text.secondary",
					"&:hover, &:focus-visible": {
						color: "error.main",
						backgroundColor: (theme: Theme) =>
							alpha(theme.palette.error.main, 0.08),
					},
				},
			},
		}[emphasis];

		return (
			<Tooltip key={key} title={allowed ? "" : requirementLabel}>
				<span style={{ display: "inline-flex" }}>
					<Button
						variant={variant}
						disabled={!allowed}
						startIcon={definition.icon}
						onClick={definition.onClick}
						data-test-id={definition.testId}
						sx={{
							height: 40,
							textTransform: "none",
							borderRadius: 2,
							...sx,
						}}
					>
						{definition.label}
					</Button>
				</span>
			</Tooltip>
		);
	};

	const actionsFooter =
		visibleActions.length > 0 ? (
			<Box
				sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
			>
				{destructiveActions.map((destructiveAction) =>
					renderAction(destructiveAction, "destructive"),
				)}
				<Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
					{flowActions
						.slice(1)
						.map((flowAction) => renderAction(flowAction, "secondary"))}
					{flowActions[0] && renderAction(flowActions[0], "primary")}
				</Box>
			</Box>
		) : null;

	useEffect(() => {
		if (modal !== "disassembly") {
			reset(carrier || CARRIER_INITIAL_VALUES);
		}
	}, [modal, carrier]);

	useMesfRealtime({
		onReceiveMessage: async (author, message) => {
			if (message.replaceAll("[", "").replaceAll("]", "") == "M2.GetCarriers") {
				await queryClient.invalidateQueries({ queryKey: ["carriersAssembly"] });
			}
		},
	});

	useEffect(() => {
		registerConfig({
			id: "lastInstancesContext",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (isError) {
			setError(e?.message);
		}
	}, [e, isError]);

	return (
		<>
			{carrier === null && modal !== "disassembly" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a carrier</Typography>
				</div>
			) : (
				<form>
					<Grid2
						container
						spacing={1}
						style={{ padding: 20 }}
						justifyContent="center"
					>
						<Grid2
							size={{ md: 12 }}
							data-test-id={`carrier-card-${assemblyStatusCodes
								.find((s) => s.Code === carrier?.CarrierStatusCode)
								?.Name.replaceAll(" ", "-")
								.toLowerCase()}`}
						>
							<CarrierCard carrier={carrier} footer={actionsFooter} />
						</Grid2>
						<Grid2
							container
							size={12}
							justifyContent="space-between"
							alignItems="center"
						>
							<Grid2>
								<Typography variant="h6" sx={{ fontWeight: "bold" }}>
									Last Carrier Instances
								</Typography>
								<Typography
									variant="body2"
									sx={{ fontSize: "0.7rem", mt: -0.5 }}
									color="textSecondary"
								>
									Double click on a row to view the form
								</Typography>
							</Grid2>
							<Grid2 justifyContent="flex-end" display="flex">
								<FormControlLabel
									control={
										<Checkbox
											checked={showAllCarriers}
											onChange={(e, checked) => setShowAllCarriers(checked)}
											size="small"
										/>
									}
									label="Show all"
								/>
							</Grid2>
						</Grid2>
						<Grid2 size={{ md: 12 }} style={{ height: "45vh" }}>
							<AgGridReact<Carrier>
								loading={isLoading}
								rowData={
									rows?.filter((r) => {
										if (showAllCarriers) {
											return true;
										}
										return (
											r?.InstalledAt != null ||
											r.CarrierInstanceId == carrier?.CarrierInstanceId
										);
									}) || []
								}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={34}
								animateRows
								getRowId={(params) => String(params.data.CreatedAt)}
								getRowStyle={getRowStyle}
								onRowDoubleClicked={(params) => {
									navigate(
										`/${routeCarrierAssembly}/${params.data?.PartId}/view/${params.data?.CarrierInstanceId}`,
									);
								}}
								getContextMenuItems={(params) => {
									const data = params.node?.data;
									showContextMenu(event as any, data, "lastInstancesContext");
									return [];
								}}
								loadingOverlayComponent={CenteredLazyLoading}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: false,
										isRowSelectable: () => false,
									},
								}}
							/>
						</Grid2>
					</Grid2>
				</form>
			)}
			<ConfirmationDialogRaw
				title={`Confirm ${action["action"]}`}
				open={openConfirmDialog}
				message={
					action["action"] == "restart"
						? "Restarting discards the current build form and every answer on it. This cannot be undone."
						: `Are you sure you want to ${action["action"]} the carrier?`
				}
				onClose={(value?: string) => {
					if (value == "OK") {
						updateStatus.mutate({ newStatus: action["code"] });
					}
					setOpenConfirmDialog(false);
				}}
			/>
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ActionModal
				open={openInstallUninstallDialog}
				onHide={() => setOpenInstallUninstallDialog(false)}
				setNotificationMessage={setNotificationMessage}
				partId={carrier?.PartId!}
				partName={carrier?.PartName!}
				carrierInstanceId={carrier?.CarrierInstanceId}
				action={action}
				setWarningMessage={setWarningMessage}
			/>
			<WarningModal
				open={warningMessage !== ""}
				title="Warning"
				message={warningMessage}
				onClose={() => setWarningMessage("")}
				suffixTitle="Carrier Status"
			/>

			<PassChangeModal
				open={openPassChangeDialog}
				onHide={() => setOpenPassChangeDialog(false)}
				setNotificationMessage={setNotificationMessage}
				carrier={{
					partId: carrier?.PartId!,
					carrierInstanceId: carrier?.CarrierInstanceId!,
				}}
			/>
		</>
	);
};
